import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import DocumentUploadArea from "../components/DocumentUploadArea";
import {
  formatBytes,
  inferTypeFromName,
  PdfPreview,
  typeIconFor,
} from "../components/DocumentPanelShared";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

// Server-side document upload is intentionally not wired yet — this component
// holds documents in local component state so the UI can be reviewed before
// the persistence contract is settled. Replace `addLocalDocuments` and the
// preview/open handlers with real API calls when the server is ready.

interface ProductDocument {
  type: string;
  synopsis: string;
  url: string;
  /** ManagedFile._id — used to thread supersededBy version chains. */
  id?: string;
  /** ManagedFile.supersededBy: the _id of the NEWER version that replaces this
   *  one, or null when this is the current version. */
  supersededBy?: string | null;
  /** Optional ManagedFile.summary surfaced under the name in the table. */
  summary?: string;
  /** File size in bytes (from ManagedFile). */
  size?: number;
  /** Upload/last-modified timestamp (ISO string or Date). */
  uploadedAt?: string;
}

interface AssetDocumentRow {
  id: string;
  name: string;
  size?: number;
  type: string;
  role: string;
  uploadedAt?: string;
  previewUrl?: string;
  source: "asset" | "product";
  url?: string;
  /** Server-side ManagedFile.summary (German); rendered under name in muted text. */
  summary?: string;
  /** Version-chain position, when this doc belongs to a supersededBy chain of >1. */
  versionIndex?: number;   // 1 = oldest … chainSize = current
  chainSize?: number;
  isCurrent?: boolean;     // newest in a multi-version chain
  isSuperseded?: boolean;  // an older, replaced version
}

const ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
};

const ROLE_KEYS = ["manual", "datasheet", "certificate", "approval", "drawing", "other"] as const;
type RoleKey = (typeof ROLE_KEYS)[number];

interface Props {
  assetId?: string;
  productDocuments?: ProductDocument[];
  onOpenProductDocument?: (url: string) => void;
  hideDropzone?: boolean;
}

export default function AssetDocumentsPanel({
  productDocuments,
  onOpenProductDocument,
  hideDropzone,
}: Props) {
  const { t } = useTranslation();
  const [assetDocuments, setAssetDocuments] = useState<AssetDocumentRow[]>([]);

  const addLocalDocuments = useCallback((files: File[]) => {
    setAssetDocuments((prev) => [
      ...prev,
      ...files.map<AssetDocumentRow>((f) => ({
        id: `${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name,
        size: f.size,
        type: inferTypeFromName(f.name),
        role: "manual" as RoleKey,
        uploadedAt: new Date().toISOString(),
        // URL.createObjectURL keeps the file accessible for preview/open
        // until the page reloads — sufficient for the dummy UI.
        previewUrl: URL.createObjectURL(f),
        source: "asset",
      })),
    ]);
  }, []);

  const setRole = useCallback(
    (id: string, role: string) =>
      setAssetDocuments((prev) =>
        prev.map((d) => (d.id === id ? { ...d, role } : d)),
      ),
    [],
  );

  const openRow = useCallback(
    (row: AssetDocumentRow) => {
      if (row.source === "product") {
        if (row.url && onOpenProductDocument) onOpenProductDocument(row.url);
      } else if (row.previewUrl) {
        window.open(row.previewUrl, "_blank", "noopener,noreferrer");
      }
    },
    [onOpenProductDocument],
  );

  const removeDocument = useCallback(
    (id: string) =>
      setAssetDocuments((prev) => {
        const target = prev.find((d) => d.id === id);
        if (target?.previewUrl) {
          try {
            URL.revokeObjectURL(target.previewUrl);
          } catch {
            /* noop */
          }
        }
        return prev.filter((d) => d.id !== id);
      }),
    [],
  );

  const rows = useMemo<AssetDocumentRow[]>(() => {
    const pdocs = productDocuments || [];
    const mapDoc = (d: ProductDocument): AssetDocumentRow => {
      const date = d.uploadedAt ? new Date(d.uploadedAt as any) : null;
      return {
        id: `product-${d.url}`,
        name: d.synopsis,
        type: inferTypeFromName(d.synopsis),
        role: d.type,
        source: "product",
        url: d.url,
        summary: d.summary,
        size: d.size,
        uploadedAt: date && !isNaN(date.getTime()) ? date.toISOString() : undefined,
      };
    };

    // Thread ManagedFile.supersededBy version chains. `X.supersededBy = Y` means
    // Y is the NEWER version that replaces X; the version with no present successor
    // is the current one. We render each chain as: current version first, then its
    // older versions descending (indented, badged). Docs without an id, or not part
    // of a chain, render as ordinary single rows — so behaviour is unchanged when
    // there is no version history.
    const withId = pdocs.filter((d) => d.id);
    const byId = new Map(withId.map((d) => [d.id as string, d]));
    const predOf = new Map<string, ProductDocument>(); // newerId -> the doc it replaces
    withId.forEach((d) => {
      if (d.supersededBy && byId.has(d.supersededBy)) predOf.set(d.supersededBy, d);
    });
    const isSuperseded = (d: ProductDocument) =>
      !!(d.supersededBy && byId.has(d.supersededBy));

    const productRows: AssetDocumentRow[] = [];
    // un-chainable docs (no id) keep original behaviour
    pdocs.filter((d) => !d.id).forEach((d) => productRows.push(mapDoc(d)));
    // heads = current versions; walk predecessors to build each chain oldest→newest
    withId
      .filter((d) => !isSuperseded(d))
      .forEach((head) => {
        const chain: ProductDocument[] = [head];
        let cursor: ProductDocument | undefined = head;
        const guard = new Set<string>();
        while (cursor?.id && !guard.has(cursor.id)) {
          guard.add(cursor.id);
          const pred = predOf.get(cursor.id);
          if (!pred) break;
          chain.unshift(pred);
          cursor = pred;
        }
        const n = chain.length;
        for (let i = n - 1; i >= 0; i--) {
          productRows.push({
            ...mapDoc(chain[i]),
            versionIndex: i + 1,
            chainSize: n,
            isCurrent: n > 1 && i === n - 1,
            isSuperseded: n > 1 && i < n - 1,
          });
        }
      });

    return [...productRows, ...assetDocuments];
  }, [productDocuments, assetDocuments]);

  const columns = useMemo<ColumnDef<AssetDocumentRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("assetPropertiesPanel.documents.columnName"),
        // Capped width so the column doesn't blow out when summaries are long.
        // Sorting/filtering still keys on `name` (the accessorKey); the summary
        // line renders below it and wraps within the column.
        size: 360,
        minSize: 220,
        maxSize: 360,
        cell: ({ row }) => (
          <div
            className="min-w-0"
            style={{ maxWidth: 360, paddingLeft: row.original.isSuperseded ? 18 : 0 }}
          >
            <div className="flex items-center gap-1.5">
              {row.original.isSuperseded ? (
                <span className="text-muted-foreground" title="Frühere Fassung">↳</span>
              ) : null}
              <div className="truncate" title={row.original.name}>
                {row.original.name}
              </div>
            </div>
            {row.original.isCurrent ? (
              <span className="mt-0.5 inline-block rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {t("assetPropertiesPanel.documents.versionCurrent", {
                  n: row.original.chainSize,
                })}
              </span>
            ) : row.original.isSuperseded ? (
              <span className="mt-0.5 inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {t("assetPropertiesPanel.documents.versionSuperseded", {
                  i: row.original.versionIndex,
                  n: row.original.chainSize,
                })}
              </span>
            ) : null}
            {row.original.summary ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="text-xs text-muted-foreground line-clamp-3 cursor-help"
                      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {row.original.summary}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    className="max-w-md whitespace-normal break-words text-xs leading-snug"
                  >
                    {row.original.summary}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: t("assetPropertiesPanel.documents.columnType"),
        size: 110,
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {typeIconFor(row.original.type)}
            <span className="text-sm">{row.original.type}</span>
          </div>
        ),
      },
      {
        accessorKey: "size",
        header: t("assetPropertiesPanel.documents.columnSize"),
        size: 110,
        cell: ({ row }) => formatBytes(row.original.size),
      },
      {
        accessorKey: "uploadedAt",
        header: t("assetPropertiesPanel.documents.columnUploadedAt"),
        size: 170,
        cell: ({ row }) =>
          row.original.uploadedAt
            ? dayjs(row.original.uploadedAt).format("DD.MM.YYYY HH:mm")
            : "—",
      },
      {
        id: "role",
        header: t("assetPropertiesPanel.documents.columnRole"),
        size: 200,
        cell: ({ row }) =>
          row.original.source === "asset" ? (
            <Select
              value={row.original.role}
              onValueChange={(v) => setRole(row.original.id, v)}
            >
              <SelectTrigger className="h-9 w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLE_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {t(`assetPropertiesPanel.documents.role.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm">{row.original.role}</div>
          ),
      },
      {
        id: "preview",
        header: t("assetPropertiesPanel.documents.columnPreview"),
        size: 170,
        cell: ({ row }) => {
          const onClick = () => openRow(row.original);
          if (row.original.type === "PDF") {
            return (
              <div
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onClick();
                }}
                className="relative cursor-pointer"
                style={{ width: 100, height: 140 }}
                title={row.original.name}
              >
                <PdfPreview
                  name={row.original.name}
                  previewUrl={row.original.previewUrl}
                  url={row.original.url}
                />
                <div
                  className="absolute inset-0"
                  style={{ background: "transparent" }}
                />
              </div>
            );
          }
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onClick();
              }}
              className="flex cursor-pointer items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground hover:bg-muted/70"
              style={{ width: 100, height: 140 }}
              title={row.original.name}
            >
              {row.original.type}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: t("assetPropertiesPanel.documents.columnActions"),
        size: 120,
        cell: ({ row }) =>
          row.original.source === "asset" ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => removeDocument(row.original.id)}
            >
              {t("global.delete")}
            </Button>
          ) : null,
      },
    ],
    [t, setRole, removeDocument, openRow],
  );

  return (
    <div className="marginTopM">
      {hideDropzone ? null : (
        <DocumentUploadArea onFiles={addLocalDocuments} accept={ACCEPT} />
      )}
      <div className={hideDropzone ? "" : "marginTopM"}>
        <DataTable
          columns={columns}
          data={rows}
          emptyMessage={t("assetPropertiesPanel.documents.empty")}
        />
      </div>
    </div>
  );
}
